/**
 * Waveform Lane Component
 */

import React, { useEffect, useRef } from "react";
import { Track, DraftTrack } from "@jamroom/shared";
import { usePlaybackStore } from "../store/playbackStore";
import "./WaveformLane.css";

interface WaveformLaneProps {
  track?: Track;
  draft?: DraftTrack;
  isDraft: boolean;
  contentWidthPx: number;
}

const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const sampleNoise = (index: number, seed: number): number => {
  const n = Math.sin((index + seed) * 12.9898) * 43758.5453;
  return n - Math.floor(n);
};

const WaveformLane: React.FC<WaveformLaneProps> = ({ track, draft, isDraft, contentWidthPx }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { zoom, playheadMs } = usePlaybackStore();

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const laneHeight = 100;
    const width = Math.max(1, Math.floor(contentWidthPx));
    if (canvas.width !== width) {
      canvas.width = width;
    }
    if (canvas.height !== laneHeight) {
      canvas.height = laneHeight;
    }

    const durationMs = track?.durationMs || draft?.durationMs || 0;
    const offsetMs = track?.offsetMs || draft?.offsetMs || 0;
    const startX = Math.max(0, Math.round((offsetMs / 1000) * zoom));
    const waveformWidth = Math.max(2, Math.round((durationMs / 1000) * zoom));

    ctx.fillStyle = isDraft ? "#1f1f0f" : "#15161a";
    ctx.fillRect(0, 0, width, laneHeight);

    ctx.strokeStyle = "#2e333b";
    ctx.beginPath();
    ctx.moveTo(0, laneHeight / 2);
    ctx.lineTo(width, laneHeight / 2);
    ctx.stroke();

    const id = track?.id || draft?.localId || "lane";
    const seed = hashString(id);
    ctx.strokeStyle = isDraft ? "#f2b84a" : "#00d4ff";
    ctx.lineWidth = 1.6;
    ctx.beginPath();

    const endX = Math.min(width, startX + waveformWidth);
    ctx.moveTo(startX, laneHeight / 2);
    for (let x = startX; x <= endX; x += 2) {
      const relative = Math.max(0, x - startX);
      const wavePhase = relative / 16;
      const noise = sampleNoise(Math.floor(relative / 2), seed) - 0.5;
      const envelope = 0.28 + 0.18 * Math.sin(relative / Math.max(20, waveformWidth) * Math.PI);
      const amp = (Math.sin(wavePhase) * 0.45 + noise * 0.55) * envelope;
      const y = laneHeight / 2 - amp * laneHeight;
      ctx.lineTo(x, y);
    }
    ctx.stroke();

    const playheadX = Math.round((playheadMs / 1000) * zoom);
    ctx.strokeStyle = "rgba(0, 212, 255, 0.9)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(playheadX, 0);
    ctx.lineTo(playheadX, laneHeight);
    ctx.stroke();
  }, [zoom, playheadMs, track, draft, isDraft, contentWidthPx]);

  return (
    <div className={`waveform-lane ${isDraft ? "draft" : ""}`}>
      <canvas
        ref={canvasRef}
        width={contentWidthPx}
        height={100}
        className="waveform-canvas"
      />
    </div>
  );
};

export default WaveformLane;
